import React, { useContext, useMemo } from 'react';
import {
  Paper,
  Box,
  Breadcrumbs,
  Link,
  Typography,
  IconButton,
  Button,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { ThemeContext } from '../contexts/ThemeContext';
import UserProfileMenu from './UserProfileMenu';

interface CachedProject {
  id?: string | number;
  name?: string;
  location?: string;
  status?: string;
  dashboard_url?: string;
}

interface ProjectPageHeaderProps {
  /** 현재 페이지를 breadcrumb 마지막 항목에 표시할 라벨 (예: "맵 에디터"). */
  currentPageLabel: string;
  /** breadcrumb 마지막 항목 좌측 아이콘. 선택. */
  currentPageIcon?: React.ReactNode;
  /**
   * 오른쪽 액션 영역에 추가로 끼워넣을 노드. 기본 우측 영역(테마 토글, 현장 설정,
   * 대시보드, UserMenu) 앞쪽에 prepend 된다. 페이지별 zoom/save 등 액션을 여기에
   * 넣어 같은 헤더 안에서 표현할 수 있다.
   */
  rightExtras?: React.ReactNode;
  /** 기본 액션(현장 설정/대시보드 버튼) 숨김. 페이지에 따라 단축 헤더 원할 때. */
  hideDefaultActions?: boolean;
  /** 좌측 breadcrumb 앞에 prepend 할 노드 — 예: 뒤로가기 아이콘. */
  leftExtras?: React.ReactNode;
  /**
   * 테마 토글 버튼 숨김. 페이지가 자체 ThemeContext(예: 맵 에디터의
   * MapEditorThemeProvider) 를 가져 글로벌 토글과 충돌하는 경우 사용.
   */
  hideThemeToggle?: boolean;
}

/**
 * 모든 현장 하위 페이지 공통 헤더.
 *
 *   [좌] 현장 목록 > [현장명] > [현재 페이지]   [위치]  [상태 chip]
 *   [우] [페이지별 액션] [🌓] [현장 설정] [대시보드] [👤]
 *
 * 사용처: ProjectDashboardPage, MapEditor, ROIEditor, LiveStatus, FileRepository.
 * localStorage 의 selectedProject 캐시를 읽어 현장명/위치/상태를 즉시 채우고,
 * breadcrumb 의 각 단계는 클릭 가능 (홈/현장 페이지로 이동).
 */
const ProjectPageHeader: React.FC<ProjectPageHeaderProps> = ({
  currentPageLabel,
  currentPageIcon,
  rightExtras,
  hideDefaultActions,
  leftExtras,
  hideThemeToggle,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode, toggleTheme } = useContext(ThemeContext);
  const { projectId: routeProjectId } = useParams<{ projectId: string }>();

  // 라우트 param 이 없을 때(중첩 라우터 밖에서 호출되는 경우)는 localStorage 폴백.
  const projectId =
    routeProjectId ||
    localStorage.getItem('selectedProjectId') ||
    '';

  const projectData = useMemo<CachedProject | null>(() => {
    try {
      const raw = localStorage.getItem('selectedProject');
      if (!raw) return null;
      return JSON.parse(raw) as CachedProject;
    } catch {
      return null;
    }
  }, []);

  const statusChip = (() => {
    const s = projectData?.status;
    if (!s) return null;
    const label = s === 'active' ? '● 운영중' : s === 'maintenance' ? '◐ 점검중' : '○ 비활성';
    const color = s === 'active' ? 'success' : 'default';
    return <Chip size="small" label={label} color={color as 'success' | 'default'} variant="outlined" />;
  })();

  return (
    <Paper
      elevation={0}
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: 4,
        py: 1.5,
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        zIndex: 5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
        {leftExtras}
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate('/projects')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            현장 목록
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => projectId && navigate(`/project/${projectId}`)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: projectId ? 'pointer' : 'default',
              fontWeight: 500,
            }}
          >
            <BusinessIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {projectData?.name || (projectId ? `프로젝트 ${projectId}` : '현장')}
          </Link>
          <Typography
            color="text.primary"
            sx={{
              display: 'flex',
              alignItems: 'center',
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {currentPageIcon && (
              <Box sx={{ mr: 0.5, display: 'flex' }}>{currentPageIcon}</Box>
            )}
            {currentPageLabel}
          </Typography>
        </Breadcrumbs>
        {projectData?.location && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {projectData.location}
          </Typography>
        )}
        {statusChip}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {rightExtras}
        {!hideThemeToggle && (
          <IconButton
            onClick={toggleTheme}
            size="small"
            title={mode === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </IconButton>
        )}
        {!hideDefaultActions && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SettingsIcon />}
              onClick={() => projectId && navigate(`/project/${projectId}/settings`)}
              disabled={!projectId}
              sx={{ borderRadius: 2 }}
            >
              현장 설정
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DashboardIcon />}
              onClick={() => {
                if (projectData?.dashboard_url) {
                  window.open(projectData.dashboard_url, '_blank', 'noopener,noreferrer');
                }
              }}
              disabled={!projectData?.dashboard_url}
              title={projectData?.dashboard_url ? '대시보드 열기' : '대시보드 URL 미등록'}
              sx={{ borderRadius: 2 }}
            >
              대시보드
            </Button>
          </>
        )}
        <UserProfileMenu />
      </Box>
    </Paper>
  );
};

export default ProjectPageHeader;
